import { useState } from 'react';
import { useFormConfigurations } from '@/hooks/useFormConfigurations';
import type { FormQuestion, FormFieldType } from '@/types';

const formLabels: Record<string, { label: string; icon: string }> = {
  epi: { label: 'Solicitação de EPI', icon: 'ri-tools-line' },
  incident: { label: 'Registro de Incidente', icon: 'ri-alarm-warning-line' },
  denunciation: { label: 'Denúncia', icon: 'ri-shield-user-line' },
};

const fieldTypeLabels: Record<FormFieldType, string> = {
  texto_curto: 'Texto Curto',
  texto_longo: 'Texto Longo',
  multipla_escolha: 'Múltipla Escolha',
  checkbox: 'Checkbox',
  tabela: 'Tabela',
  upload: 'Upload de Arquivo',
  data: 'Data',
  numero: 'Número',
};

const fieldTypeIcons: Record<FormFieldType, string> = {
  texto_curto: 'ri-text',
  texto_longo: 'ri-file-text-line',
  multipla_escolha: 'ri-list-check',
  checkbox: 'ri-checkbox-line',
  tabela: 'ri-table-line',
  upload: 'ri-upload-cloud-line',
  data: 'ri-calendar-line',
  numero: 'ri-hashtag',
};

function generateId(): string {
  return 'q-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 7);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminQuestionarios() {
  const { configs, getForm, updateQuestions } = useFormConfigurations();
  const [activeForm, setActiveForm] = useState<'epi' | 'incident' | 'denunciation'>('epi');
  const [editingQuestion, setEditingQuestion] = useState<FormQuestion | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const currentForm = getForm(activeForm);
  const sortedQuestions = currentForm
    ?[...currentForm.questions].sort((a, b) => a.order - b.order)
    : [];

  const handleAdd = () => {
    setEditingQuestion({
      id: generateId(),
      label: '',
      type: 'texto_curto',
      required: false,
      options: [],
      tableHeaders: [],
      tableRows: [],
      order: sortedQuestions.length,
    });
    setIsNew(true);
    setShowModal(true);
  };

  const handleEdit = (question: FormQuestion) => {
    setEditingQuestion({ ...question });
    setIsNew(false);
    setShowModal(true);
  };

  const handleDelete = (id: string) => {
    const filtered = currentForm!.questions.filter((q) => q.id !== id);
    const reordered = filtered.map((q, idx) => ({ ...q, order: idx }));
    updateQuestions(activeForm, reordered);
  };

  const handleMoveUp = (id: string) => {
    const idx = sortedQuestions.findIndex((q) => q.id === id);
    if (idx <= 0) return;
    const list = [...sortedQuestions];
    [list[idx - 1], list[idx]] = [list[idx], list[idx - 1]];
    const reordered = list.map((q, i) => ({ ...q, order: i }));
    updateQuestions(activeForm, reordered);
  };

  const handleMoveDown = (id: string) => {
    const idx = sortedQuestions.findIndex((q) => q.id === id);
    if (idx >= sortedQuestions.length - 1) return;
    const list = [...sortedQuestions];
    [list[idx], list[idx + 1]] = [list[idx + 1], list[idx]];
    const reordered = list.map((q, i) => ({ ...q, order: i }));
    updateQuestions(activeForm, reordered);
  };

  const handleSaveQuestion = (question: FormQuestion) => {
    if (!currentForm) return;
    if (isNew) {
      updateQuestions(activeForm, [...currentForm.questions, question]);
    } else {
      const updated = currentForm.questions.map((q) =>
        q.id === question.id ?question : q
      );
      updateQuestions(activeForm, updated);
    }
    setShowModal(false);
    setEditingQuestion(null);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
        <div>
          <h1 className="text-xl font-bold text-portal-gray-900">Editor de Questionários</h1>
          <p className="text-sm text-portal-gray-500">
            Configure os campos de cada formulário. Versão atual: {currentForm?.version || 1}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-portal-gray-400">
            Atualizado: {currentForm ?formatDate(currentForm.updatedAt) : '-'}
          </span>
        </div>
      </div>

      {/* Form Type Selector */}
      <div className="flex items-center gap-1 bg-portal-gray-100 rounded-xl p-1 mb-6 w-fit">
        {(['epi', 'incident', 'denunciation'] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setActiveForm(key);
              setShowModal(false);
              setEditingQuestion(null);
            }}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
              activeForm === key
                ?'bg-white text-portal-gray-800'
                : 'text-portal-gray-500 hover:text-portal-gray-700'
            }`}
          >
            <i className={`${formLabels[key].icon} text-sm`}></i>
            {formLabels[key].label}
          </button>
        ))}
      </div>

      {/* Questions List */}
      <div className="bg-white rounded-xl border border-portal-gray-200 overflow-hidden mb-4">
        <div className="px-5 py-3 border-b border-portal-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="ri-question-line text-portal-purple text-sm"></i>
            <span className="text-sm font-medium text-portal-gray-700">
              Campos do Formulário
            </span>
            <span className="text-xs text-portal-gray-400">
              ({sortedQuestions.length} campos)
            </span>
          </div>
          <button
            type="button"
            onClick={handleAdd}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-portal-purple rounded-lg hover:bg-portal-purple-dark transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-sm"></i>
            Novo Campo
          </button>
        </div>

        {sortedQuestions.length === 0 ?(
          <div className="p-12 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-portal-gray-100 flex items-center justify-center">
              <i className="ri-question-line text-portal-gray-400 text-xl"></i>
            </div>
            <p className="text-sm text-portal-gray-500">Nenhum campo configurado.</p>
            <button
              type="button"
              onClick={handleAdd}
              className="mt-3 text-sm text-portal-purple font-medium hover:text-portal-purple-dark cursor-pointer whitespace-nowrap"
            >
              Adicionar primeiro campo
            </button>
          </div>
        ) : (
          <div className="divide-y divide-portal-gray-100">
            {sortedQuestions.map((q, idx) => (
              <div
                key={q.id}
                className="px-5 py-3.5 flex items-center gap-4 hover:bg-portal-gray-50/30 transition-colors group"
              >
                {/* Move controls */}
                <div className="flex flex-col gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => handleMoveUp(q.id)}
                    disabled={idx === 0}
                    className="w-6 h-5 flex items-center justify-center rounded text-portal-gray-400 hover:text-portal-gray-600 hover:bg-portal-gray-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-up-s-line text-xs"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveDown(q.id)}
                    disabled={idx === sortedQuestions.length - 1}
                    className="w-6 h-5 flex items-center justify-center rounded text-portal-gray-400 hover:text-portal-gray-600 hover:bg-portal-gray-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-down-s-line text-xs"></i>
                  </button>
                </div>

                {/* Order number */}
                <span className="text-xs font-medium text-portal-gray-400 w-5 text-center flex-shrink-0">
                  {idx + 1}
                </span>

                {/* Type icon */}
                <div className="w-8 h-8 rounded-lg bg-portal-gray-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${fieldTypeIcons[q.type]} text-portal-gray-500 text-sm`}></i>
                </div>

                {/* Label + type + required */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-portal-gray-800 truncate">
                    {q.label || <span className="text-portal-gray-400 italic">Sem título</span>}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-portal-gray-400">{fieldTypeLabels[q.type]}</span>
                    {q.required && (
                      <span className="text-xs text-red-500 font-medium">• Obrigatório</span>
                    )}
                    {(q.type === 'multipla_escolha' || q.type === 'checkbox') && q.options && q.options.length > 0 && (
                      <span className="text-xs text-portal-gray-400">
                        • {q.options.length} opções
                      </span>
                    )}
                    {q.type === 'tabela' && q.tableRows && q.tableRows.length > 0 && (
                      <span className="text-xs text-portal-gray-400">
                        • {q.tableRows.length} linhas
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleEdit(q)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-portal-gray-400 hover:text-portal-purple hover:bg-portal-purple-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-pencil-line text-sm"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(q.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-portal-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview */}
      <div className="bg-white rounded-xl border border-portal-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-portal-gray-100 flex items-center gap-2">
          <i className="ri-eye-line text-portal-gray-400 text-sm"></i>
          <span className="text-sm font-medium text-portal-gray-700">Pré-visualização</span>
        </div>
        <div className="p-5 space-y-4 max-w-lg">
          {sortedQuestions.map((q) => (
            <div key={q.id}>
              <label className="block text-sm font-medium text-portal-gray-700 mb-1.5">
                {q.label || 'Sem título'}
                {q.required && <span className="text-red-400 ml-0.5">*</span>}
              </label>
              {q.type === 'texto_curto' && (
                <input
                  type="text"
                  readOnly
                  placeholder="Resposta curta..."
                  className="w-full text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-portal-gray-50 text-portal-gray-400"
                />
              )}
              {q.type === 'texto_longo' && (
                <textarea
                  readOnly
                  rows={3}
                  placeholder="Resposta longa..."
                  className="w-full text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-portal-gray-50 text-portal-gray-400 resize-none"
                />
              )}
              {q.type === 'numero' && (
                <input
                  type="number"
                  readOnly
                  placeholder="0"
                  className="w-24 text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-portal-gray-50 text-portal-gray-400"
                />
              )}
              {(q.type === 'multipla_escolha' || q.type === 'checkbox') && (
                <div className="space-y-1.5">
                  {(q.options || []).map((opt, i) => (
                    <label key={i} className="flex items-center gap-2 cursor-default">
                      <input
                        type={q.type === 'checkbox' ?'checkbox' : 'radio'}
                        readOnly
                        disabled
                        name={q.id}
                        className="opacity-50"
                      />
                      <span className="text-sm text-portal-gray-500">{opt}</span>
                    </label>
                  ))}
                  {(q.options || []).length === 0 && (
                    <p className="text-sm text-portal-gray-400 italic">Sem opções configuradas</p>
                  )}
                </div>
              )}
              {q.type === 'tabela' && (
                <div className="border border-portal-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-portal-gray-100">
                        <th className="text-left text-xs font-medium text-portal-gray-500 px-3 py-2">Item</th>
                        {(q.tableHeaders || []).map((h, i) => (
                          <th key={i} className="text-center text-xs font-medium text-portal-gray-500 px-3 py-2">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(q.tableRows || []).map((r, i) => (
                        <tr key={i} className="border-t border-portal-gray-50">
                          <td className="px-3 py-1.5 text-portal-gray-700">{r}</td>
                          {(q.tableHeaders || []).map((_, j) => (
                            <td key={j} className="px-3 py-1.5 text-center">
                              <input readOnly className="w-12 text-center text-sm border border-portal-gray-200 rounded px-1 py-0.5 bg-portal-gray-50 text-portal-gray-400" />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {q.type === 'upload' && (
                <div className="border-2 border-dashed border-portal-gray-200 rounded-lg p-6 text-center cursor-default">
                  <i className="ri-upload-cloud-line text-portal-gray-300 text-2xl block mb-2"></i>
                  <p className="text-sm text-portal-gray-400">Clique para fazer upload</p>
                </div>
              )}
              {q.type === 'data' && (
                <input
                  type="date"
                  readOnly
                  className="text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-portal-gray-50 text-portal-gray-400 cursor-default"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {showModal && editingQuestion && (
        <QuestionModal
          question={editingQuestion}
          onChange={setEditingQuestion}
          onSave={() => handleSaveQuestion(editingQuestion)}
          onClose={() => {
            setShowModal(false);
            setEditingQuestion(null);
          }}
          isNew={isNew}
        />
      )}
    </div>
  );
}

function QuestionModal({
  question,
  onChange,
  onSave,
  onClose,
  isNew,
}: {
  question: FormQuestion;
  onChange: (q: FormQuestion) => void;
  onSave: () => void;
  onClose: () => void;
  isNew: boolean;
}) {
  const [newOption, setNewOption] = useState('');
  const [newTableHeader, setNewTableHeader] = useState('');
  const [newTableRow, setNewTableRow] = useState('');

  const hasOptions = question.type === 'multipla_escolha' || question.type === 'checkbox';
  const hasTable = question.type === 'tabela';

  const addOption = () => {
    if (!newOption.trim()) return;
    onChange({ ...question, options: [...(question.options || []), newOption.trim()] });
    setNewOption('');
  };

  const removeOption = (idx: number) => {
    onChange({
      ...question,
      options: (question.options || []).filter((_, i) => i !== idx),
    });
  };

  const addTableHeader = () => {
    if (!newTableHeader.trim()) return;
    onChange({ ...question, tableHeaders: [...(question.tableHeaders || []), newTableHeader.trim()] });
    setNewTableHeader('');
  };

  const removeTableHeader = (idx: number) => {
    onChange({
      ...question,
      tableHeaders: (question.tableHeaders || []).filter((_, i) => i !== idx),
    });
  };

  const addTableRow = () => {
    if (!newTableRow.trim()) return;
    onChange({ ...question, tableRows: [...(question.tableRows || []), newTableRow.trim()] });
    setNewTableRow('');
  };

  const removeTableRow = (idx: number) => {
    onChange({
      ...question,
      tableRows: (question.tableRows || []).filter((_, i) => i !== idx),
    });
  };

  const handleSave = () => {
    if (!question.label.trim()) return;
    onSave();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="px-6 py-5 border-b border-portal-gray-100 flex items-center justify-between">
          <h3 className="text-base font-semibold text-portal-gray-800">
            {isNew ?'Novo Campo' : 'Editar Campo'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-portal-gray-400 hover:text-portal-gray-600 hover:bg-portal-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-portal-gray-700 mb-1.5">
              Título do campo <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={question.label}
              onChange={(e) => onChange({ ...question, label: e.target.value })}
              placeholder="Ex: Qual o seu setor?"
              className="w-full text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-800 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-portal-gray-700 mb-1.5">Tipo de campo</label>
            <select
              value={question.type}
              onChange={(e) => {
                const newType = e.target.value as FormFieldType;
                onChange({
                  ...question,
                  type: newType,
                  options: (newType === 'multipla_escolha' || newType === 'checkbox') ?(question.options || []) : [],
                  tableHeaders: newType === 'tabela' ?(question.tableHeaders || []) : [],
                  tableRows: newType === 'tabela' ?(question.tableRows || []) : [],
                });
              }}
              className="w-full text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple cursor-pointer"
            >
              {Object.entries(fieldTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Required toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onChange({ ...question, required: !question.required })}
              className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${
                question.required ?'bg-portal-purple' : 'bg-portal-gray-300'
              }`}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                  question.required ?'left-[calc(100%-1.125rem)]' : 'left-0.5'
                }`}
              ></span>
            </button>
            <span className="text-sm text-portal-gray-700">
              Campo obrigatório
            </span>
          </div>

          {/* Options (for multiple choice / checkbox) */}
          {hasOptions && (
            <div>
              <label className="block text-sm font-medium text-portal-gray-700 mb-2">Opções</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                  placeholder="Adicionar opção..."
                  className="flex-1 text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-800 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple"
                />
                <button
                  type="button"
                  onClick={addOption}
                  className="px-3 py-2 text-sm font-medium text-portal-purple border border-portal-purple rounded-lg hover:bg-portal-purple-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                </button>
              </div>
              {(question.options || []).length === 0 ?(
                <p className="text-xs text-portal-gray-400">Nenhuma opção adicionada.</p>
              ) : (
                <div className="space-y-1">
                  {(question.options || []).map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-portal-gray-50 rounded-lg px-3 py-1.5">
                      <i className="ri-drag-move-line text-portal-gray-400 text-sm cursor-grab"></i>
                      <span className="flex-1 text-sm text-portal-gray-700">{opt}</span>
                      <button
                        type="button"
                        onClick={() => removeOption(idx)}
                        className="text-portal-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-circle-line text-sm"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Table headers and rows */}
          {hasTable && (
            <div className="space-y-4">
              {/* Headers */}
              <div>
                <label className="block text-sm font-medium text-portal-gray-700 mb-2">Colunas da tabela</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newTableHeader}
                    onChange={(e) => setNewTableHeader(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTableHeader(); } }}
                    placeholder="Nome da coluna..."
                    className="flex-1 text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-800 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple"
                  />
                  <button
                    type="button"
                    onClick={addTableHeader}
                    className="px-3 py-2 text-sm font-medium text-portal-purple border border-portal-purple rounded-lg hover:bg-portal-purple-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                {(question.tableHeaders || []).length === 0 ?(
                  <p className="text-xs text-portal-gray-400">Nenhuma coluna adicionada.</p>
                ) : (
                  <div className="space-y-1">
                    {(question.tableHeaders || []).map((h, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-portal-gray-50 rounded-lg px-3 py-1.5">
                        <span className="flex-1 text-sm text-portal-gray-700">{h}</span>
                        <button
                          type="button"
                          onClick={() => removeTableHeader(idx)}
                          className="text-portal-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-close-circle-line text-sm"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Rows */}
              <div>
                <label className="block text-sm font-medium text-portal-gray-700 mb-2">Linhas da tabela</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newTableRow}
                    onChange={(e) => setNewTableRow(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTableRow(); } }}
                    placeholder="Nome da linha..."
                    className="flex-1 text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-800 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple"
                  />
                  <button
                    type="button"
                    onClick={addTableRow}
                    className="px-3 py-2 text-sm font-medium text-portal-purple border border-portal-purple rounded-lg hover:bg-portal-purple-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                {(question.tableRows || []).length === 0 ?(
                  <p className="text-xs text-portal-gray-400">Nenhuma linha adicionada.</p>
                ) : (
                  <div className="space-y-1">
                    {(question.tableRows || []).map((r, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-portal-gray-50 rounded-lg px-3 py-1.5">
                        <span className="flex-1 text-sm text-portal-gray-700">{r}</span>
                        <button
                          type="button"
                          onClick={() => removeTableRow(idx)}
                          className="text-portal-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-close-circle-line text-sm"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-portal-gray-100 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-portal-gray-600 bg-white border border-portal-gray-200 rounded-lg hover:bg-portal-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!question.label.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-portal-purple rounded-lg hover:bg-portal-purple-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
          >
            {isNew ?'Adicionar' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
